import { motion } from 'framer-motion';
import { useMemo, memo, useCallback, type ChangeEvent as ReactChangeEvent } from 'react';
import {
  Box,
  Center,
  ClickAwayListener,
  Column,
  Divider,
  Input,
  ScrollArea,
  Text,
} from '~/commonComponents';
import { useAppSelector, useToggle } from '~/hooks';
import ArrowDownIcon from '~/images/icons/ArrowDown';
import SearchIcon from '~/images/search-icon.svg';
import { Colors } from '~/themes/colors';
import { Animations, Styles } from './Constants';
import { IInputSearchOfficeProps, IOfficeOption } from './Types';
import { useOfficeSearch } from './useOfficeSearch';

import './Styles.scss';

const InputSearchOfficeComponent = ({
  placeholder = 'Select Office',
  onChange,
  value,
}: IInputSearchOfficeProps) => {
  const [isShowOptions, controls] = useToggle();

  const offices = useAppSelector((state) => state.user.childOffices || []);

  const options = useMemo(
    () =>
      offices.map((it) => ({
        label: it.office_name,
        value: it.id,
      })) as IOfficeOption[],
    [offices],
  );

  const selectedOffice = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const handleOfficeChange = useCallback(
    (office: IOfficeOption) => onChange?.(office.value),
    [onChange],
  );

  const {
    searchQuery,
    optimisticSelection,
    filteredOptions,
    inputRef,
    handleOfficeSelect,
    handleSearchChange,
  } = useOfficeSearch({
    options,
    selectedOffice,
    onOfficeSelect: handleOfficeChange,
    onCloseOptions: controls.setOff,
  });

  const handleInputChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e.target.value);
  };

  return (
    <Column position="relative">
      <button style={Styles.Trigger} type="button" onClick={controls.setOn}>
        <Center marginRight={60}>
          <Text
            color={optimisticSelection ? Colors.neutral_700 : Colors.neutral_300}
            textAlign="left"
          >
            {optimisticSelection ? optimisticSelection.label : placeholder}
          </Text>
        </Center>
        <ArrowDownIcon />
      </button>
      <ClickAwayListener onClickAway={controls.setOff}>
        <motion.div
          animate={isShowOptions ? 'show' : 'hide'}
          initial="hide"
          style={Styles.Popover}
          transition={Animations.Popover.transition}
          variants={Animations.Popover.variants}
        >
          <Column paddingInline={24} paddingTop={10} paddingBottom={10} gap={10}>
            <Input
              ref={inputRef}
              PrefixComponent={
                <img
                  src={SearchIcon}
                  style={{
                    width: 24,
                    height: 24,
                  }}
                  alt="search_icon"
                />
              }
              placeholder="Search Office"
              value={searchQuery}
              onChange={handleInputChange}
            />
          </Column>
          <Divider color={Colors.neutral_200} height={1} marginVertical={0} />
          <ScrollArea style={Styles.ScrollArea}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((office) => (
                <Box
                  className="options-item"
                  height={36}
                  justifyContent="center"
                  key={office.value}
                  paddingInline={16}
                  onClick={() => handleOfficeSelect(office)}
                >
                  <Text>{office.label}</Text>
                </Box>
              ))
            ) : (
              <Box justifyContent="center" paddingInline={16}>
                <Text color={Colors.neutral_400} textWrap="wrap">
                  Office not found. Please make sure the name is spelled correctly.
                </Text>
              </Box>
            )}
          </ScrollArea>
        </motion.div>
      </ClickAwayListener>
    </Column>
  );
};

export const InputSearchOffice = memo(InputSearchOfficeComponent);
